// MACHINERY V2 (Stroje) — active-filter chip descriptors for pattern B row 2
// (M2.16a).
//
// Turns the APPLIED filter state into the board's "Stav: Bezia + Alarm ✕" /
// "Lokacia: Hala B ✕" summaries (mobile/screenshots/01-filter-b-chosen.png), and
// owns what each ✕ undoes.
//
// >>> APPLIED, NEVER PENDING. <<< This row describes what is filtering the fleet
// the user can see right now. The sheet's draft is a different thing and lives
// in useFleetFilterDraft.ts; if this row read the draft it would claim filters
// that are not in force.
//
// ONE CHIP PER FILTER GROUP, and that count is also the Filtre badge's number —
// the board's badge reads "2" over its two chips, so the two must be derived
// from the same list rather than counted twice in two places.
//
// SEARCH IS NOT A CHIP. It has its own always-visible field in the bar above; a
// chip for it would be a second, removable copy of a control that is already on
// screen. Group-by, view and scope are not filters at all.
//
// ============================================================
//  EVERY ✕ MERGES ONTO THE CURRENT QUERY, NEVER A RENDER SNAPSHOT
// ============================================================
// `changeQuery` REPLACES the query object wholesale
// (customerMachinerySlice.ts:150-152), and the search debounce writes to that
// same object 400 ms after a keystroke. A handler closed over the render-time
// `query` therefore reverted whatever landed in that window — dismiss a chip
// just after typing and the search the user had just entered silently came
// back. `queryRef` keeps the merge base current.
//
// This is the SAME defect, with the same fix, that useFleetSearch.ts:6-12
// ("bug 1, stale merge base") records for the search box and that
// useFleetFilterDraft.ts:147-152 carries one file away — and it was
// reintroduced here in the same commit that guarded those two. Any NEW handler
// in this file that dispatches `changeQuery` merges onto `queryRef.current`.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import { useRef } from "react";

import { useAppDispatch, useAppSelector } from "../../../../shared/hooks/sharedHooks";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { MachinesQuery } from "../../models/models";
import { changeQuery } from "../../customerMachinerySlice";
import {
  clearFleetFilters,
  setFleetLocationFilter,
  setFleetStatusFilters,
} from "../machineryV2UiSlice";
import { ActiveFilterChip } from "../mobile/ActiveFilterChips";
import { FleetPageData } from "./fleetPageData";
import { FLEET_STATE_FILTERS } from "./fleetStatusFilters";
import { getMachineTypeLabel } from "../../../../shared/utils/machineUtils";

/** Display name for a location-tree node id ("Plant" or "Plant>Hall"). The
 *  board prints the LEAF name only — the parent is implied by the hall. */
const locationName = (data: FleetPageData, id: string): string => {
  for (const node of data.locations) {
    if (node.id === id) return node.name;
    for (const child of node.children) if (child.id === id) return child.name;
  }
  return id;
};

export interface FleetActiveFilters {
  chips: ActiveFilterChip[];
  clearAll: () => void;
}

export const useFleetActiveFilters = (data: FleetPageData): FleetActiveFilters => {
  const dispatch = useAppDispatch();
  const { translate } = useTranslation("myMachinery");
  const query = useAppSelector((s) => s.customerMachinery.query);
  const ui = useAppSelector((s) => s.machineryV2Ui);

  // The always-current merge base — see the header. Read for WRITES only; the
  // chip LABELS below read `query` directly, which is correct because a label
  // must describe the render it is part of.
  const queryRef = useRef<MachinesQuery>(query);
  queryRef.current = query;

  const chips: ActiveFilterChip[] = [];

  if (ui.fleetStatusFilters.length > 0) {
    // The SAME vocabulary the sheet and the rail offer — FLEET_STATE_FILTERS is
    // the one table, so a chip can never name a state differently from the row
    // that set it. Order follows that table (urgency first), not click order.
    const names = FLEET_STATE_FILTERS.filter(
      (s) => ui.fleetStatusFilters.indexOf(s.state) >= 0
    ).map((s) => translate(s.labelKey));
    chips.push({
      id: "status",
      label: `${translate("myMachinery:v2.fleet.status")}: ${names.join(" + ")}`,
      onRemove: () => dispatch(setFleetStatusFilters([])),
    });
  }

  if (query.typeId) {
    const type = data.types.find((t) => t.id === query.typeId);
    chips.push({
      id: "type",
      label: `${translate("myMachinery:machineType")}: ${type ? getMachineTypeLabel(type, translate) : query.typeId}`,
      // Dropping the type drops the manufacturer with it: the manufacturer list
      // is derived from the selected type (fleetSelectors.derivedManufacturers),
      // so a surviving manufacturer would filter by an option nothing offers.
      // Same pairing the rail's onSelectType makes (FleetShell.tsx:101-103).
      onRemove: () =>
        dispatch(
          changeQuery({ ...queryRef.current, typeId: undefined, manufacturerId: undefined })
        ),
    });
  }

  if (query.manufacturerId) {
    const maker = data.manufacturers.find((m) => m.id === query.manufacturerId);
    chips.push({
      id: "manufacturer",
      label: `${translate("myMachinery:manufacturer")}: ${
        maker ? maker.name : query.manufacturerId
      }`,
      onRemove: () =>
        dispatch(changeQuery({ ...queryRef.current, manufacturerId: undefined })),
    });
  }

  if (ui.fleetLocationFilter) {
    chips.push({
      id: "location",
      label: `${translate("myMachinery:locationFilter")}: ${locationName(
        data,
        ui.fleetLocationFilter
      )}`,
      onRemove: () => dispatch(setFleetLocationFilter(null)),
    });
  }

  const clearAll = () => {
    // TWO DISPATCHES, AND BOTH ARE NEEDED. `clearFleetFilters` clears the slice's
    // status + location (and its `fleetTypeFilter`, which no surface reads — the
    // type filter that IS read lives in the shared `customerMachinery` query,
    // exactly as it does on the live grid). So the query half is cleared here.
    dispatch(clearFleetFilters());
    if (queryRef.current.typeId || queryRef.current.manufacturerId) {
      dispatch(
        changeQuery({ ...queryRef.current, typeId: undefined, manufacturerId: undefined })
      );
    }
  };

  return { chips, clearAll };
};

export default useFleetActiveFilters;
